// frontend/src/super-admin/site/HelpDocsPage.tsx
//
// Help center article list. Editing happens on HelpDocEditorPage.

import * as React from 'react';
import { Link } from 'react-router-dom';
import { Plus, FileText, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { PageHeader } from '../../design-system/components/PageHeader';
import { LoadingState } from '../../design-system/components/LoadingState';
import { ErrorState } from '../../design-system/components/ErrorState';
import { VISUAL, statusPill } from '../../design-system/visual';
import { docsApi, type HelpDoc } from '../../modules/site-settings/api';
import { useAdminPermission } from '../../core/auth/useAdminPermission';
import { useConfirm } from '../../components/ui/confirm-dialog';

export function HelpDocsPage() {
  const confirm = useConfirm();
  const canWrite = useAdminPermission('site.docs.write');
  const [docs, setDocs] = React.useState<HelpDoc[] | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [search, setSearch] = React.useState('');

  function load() {
    setError(null);
    setDocs(null);
    docsApi.list().then(setDocs).catch((e) => setError(String(e.message ?? e)));
  }

  React.useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = React.useMemo(() => {
    if (!docs) return null;
    const q = search.trim().toLowerCase();
    if (!q) return docs;
    return docs.filter((d) =>
      d.title.toLowerCase().includes(q) ||
      d.slug.toLowerCase().includes(q) ||
      d.category.toLowerCase().includes(q),
    );
  }, [docs, search]);

  async function remove(d: HelpDoc) {
    if (!(await confirm({ title: `Delete "${d.title}"?`, description: 'This cannot be undone.', destructive: true, confirmLabel: 'Delete' }))) return;
    try {
      await docsApi.remove(d.id);
      setDocs((prev) => (prev ? prev.filter((x) => x.id !== d.id) : prev));
      toast.success('Article deleted');
    } catch (err: any) {
      toast.error('Delete failed', { description: err.message ?? String(err) });
    }
  }

  return (
    <div>
      <PageHeader
        eyebrow="Site / Content"
        title="Help center"
        description="Guides, FAQs, and documentation articles for your customers."
        actions={canWrite ? (
          <Link to="/admin/site/docs/new">
            <Button className="rounded-full h-10 px-4 text-[13px] font-semibold gap-1.5">
              <Plus className="h-3.5 w-3.5" />
              New article
            </Button>
          </Link>
        ) : undefined}
      />

      <div className="px-6 sm:px-8 lg:px-10 pb-12 space-y-6">
        {error && <ErrorState description={error} onRetry={load} />}
        {docs === null && !error && <LoadingState variant="page" />}

        {docs && docs.length > 0 && (
          <>
            <Input
              type="search"
              placeholder="Search by title, slug, or category…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-10 rounded-full bg-card/80 border-border/60 text-[13px] max-w-md"
            />
            <section className={[VISUAL.bento, 'overflow-hidden'].join(' ')}>
              <ul className="divide-y divide-border/40">
                {(filtered ?? []).map((d) => (
                  <li key={d.id} className="flex items-center gap-4 px-5 sm:px-6 py-4 hover:bg-muted/40 transition-colors">
                    <div className="h-11 w-11 rounded-2xl bg-muted/50 ring-1 ring-border/40 flex items-center justify-center shrink-0">
                      <FileText className="h-4 w-4 text-muted-foreground" strokeWidth={1.7} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline gap-2 flex-wrap">
                        <Link
                          to={`/admin/site/docs/${d.id}`}
                          className="text-[14.5px] font-semibold tracking-[-0.005em] text-foreground hover:text-coral transition-colors truncate"
                        >
                          {d.title}
                        </Link>
                        <span className="text-[12px] font-mono text-muted-foreground">/{d.slug}</span>
                      </div>
                      <p className="text-[12px] text-muted-foreground mt-0.5">
                        <span className="text-muted-foreground/70">{d.category}</span>
                        {d.published_at ? ` · Published ${new Date(d.published_at).toLocaleDateString()}` : ' · Unpublished'}
                      </p>
                    </div>
                    <span className={statusPill(d.is_published ? 'success' : 'draft')}>
                      {d.is_published ? 'Live' : 'Draft'}
                    </span>
                    {canWrite && (
                      <Button
                        type="button"
                        variant="ghost"
                        onClick={() => remove(d)}
                        className="h-9 px-2.5 rounded-full text-rose-700 hover:text-rose-800"
                        aria-label={`Delete ${d.title}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </li>
                ))}
              </ul>
            </section>
          </>
        )}

        {docs && docs.length === 0 && (
          <div className={[VISUAL.bento, 'py-14 px-8 text-center max-w-md mx-auto'].join(' ')}>
            <div className="mx-auto mb-5 h-14 w-14 rounded-2xl bg-coral/15 ring-1 ring-coral/25 flex items-center justify-center">
              <FileText className="h-6 w-6 text-coral" strokeWidth={1.6} />
            </div>
            <p className="text-[18px] font-semibold tracking-[-0.01em] text-foreground">No articles yet</p>
            <p className="text-[13.5px] text-muted-foreground mt-1.5 leading-relaxed">
              Help articles, guides, and FAQs live here.
            </p>
            {canWrite && (
              <Link to="/admin/site/docs/new" className="inline-block mt-6">
                <Button className="rounded-full h-10 px-5 font-semibold gap-1.5">
                  <Plus className="h-3.5 w-3.5" /> Write first article
                </Button>
              </Link>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
